import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';

/**
 * MaintenancePage – Premium light-themed maintenance screen
 * Matches the Scrudix brand: soft lavender/cream background,
 * glassmorphic cards, bold zinc typography, dreamy blob animations.
 */

// ─── Floating orbs ───
interface Orb {
  id: number;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
  hue: number;
}

const generateOrbs = (count: number): Orb[] =>
  Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 6 + 2,
    duration: Math.random() * 12 + 8,
    delay: Math.random() * 6,
    hue: Math.random() > 0.5 ? 260 : 40, // lavender or gold
  }));

// ─── Wrench + Gear icon (single clean SVG) ───
const MaintenanceIcon: React.FC = () => (
  <div className="maint-icon-wrap">
    <div className="maint-icon-ring" />
    <div className="maint-icon-ring maint-icon-ring-2" />
    <svg className="maint-icon-svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
    <div className="maint-icon-orbit">
      <div className="maint-icon-dot" />
    </div>
  </div>
);

// ─── Animated grid of small plus signs ───
const PlusGrid: React.FC = () => {
  const items = useMemo(
    () =>
      Array.from({ length: 80 }, (_, i) => ({
        id: i,
        x: (i % 10) * 11 + 1,
        y: Math.floor(i / 10) * 13 + 2,
        delay: Math.random() * 6,
      })),
    []
  );
  return (
    <div className="maint-plus-grid" aria-hidden="true">
      {items.map((p) => (
        <span
          key={p.id}
          className="maint-plus"
          style={{ left: `${p.x}%`, top: `${p.y}%`, animationDelay: `${p.delay}s` }}
        >
          +
        </span>
      ))}
    </div>
  );
};

// ─── Progress / countdown timer ───
const CountdownTimer: React.FC<{ estimatedMinutes?: number }> = ({ estimatedMinutes = 30 }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setElapsed((p) => p + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const remaining = Math.max(0, estimatedMinutes * 60 - elapsed);
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const progress = Math.min(100, (elapsed / (estimatedMinutes * 60)) * 100);

  return (
    <div className="maint-countdown">
      <div className="maint-countdown-bar">
        <div className="maint-countdown-fill" style={{ width: `${progress}%` }}>
          <span className="maint-countdown-shine" />
        </div>
      </div>
      <div className="maint-countdown-time">
        <span className="maint-countdown-digit">{String(mins).padStart(2, '0')}</span>
        <span className="maint-countdown-colon">:</span>
        <span className="maint-countdown-digit">{String(secs).padStart(2, '0')}</span>
        <span className="maint-countdown-label">estimated remaining</span>
      </div>
    </div>
  );
};

// ─── Status steps ───
const statusSteps = [
  { label: 'Database Migration', icon: '⚙' },
  { label: 'Service Upgrade', icon: '⬆' },
  { label: 'Cache Rebuild', icon: '⟳' },
];

const StatusSteps: React.FC = () => (
  <div className="maint-steps">
    {statusSteps.map((s, i) => (
      <div key={s.label} className="maint-step" style={{ animationDelay: `${0.4 + i * 0.12}s` }}>
        <span className="maint-step-icon">{s.icon}</span>
        <span className="maint-step-dot" style={{ animationDelay: `${i * 0.5}s` }} />
        <span className="maint-step-label">{s.label}</span>
      </div>
    ))}
  </div>
);

// ─── Main Component ───
const MaintenancePage: React.FC<{
  estimatedMinutes?: number;
  message?: string;
  showCountdown?: boolean;
}> = ({
  estimatedMinutes = 30,
  message = "We're upgrading Scrudix to deliver an even better experience. Hang tight — we'll be back shortly.",
  showCountdown = true,
}) => {
  const orbs = useMemo(() => generateOrbs(25), []);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMousePos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  }, []);

  return (
    <div
      ref={containerRef}
      className="maint-root"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setMousePos({ x: 50, y: 50 })}
    >
      {/* ── Background layers ── */}
      <PlusGrid />

      {/* Soft blobs matching Scrudix brand */}
      <div className="maint-blob maint-blob-lavender" />
      <div className="maint-blob maint-blob-gold" />
      <div className="maint-blob maint-blob-rose" />

      {/* Floating orbs */}
      <div className="maint-orbs" aria-hidden="true">
        {orbs.map((o) => (
          <div
            key={o.id}
            className="maint-orb"
            style={{
              left: `${o.x}%`,
              top: `${o.y}%`,
              width: `${o.size}px`,
              height: `${o.size}px`,
              animationDuration: `${o.duration}s`,
              animationDelay: `${o.delay}s`,
              background: `hsl(${o.hue}, 60%, 72%)`,
            }}
          />
        ))}
      </div>

      {/* Cursor-follow glow */}
      <div
        className="maint-cursor-glow"
        style={{ left: `${mousePos.x}%`, top: `${mousePos.y}%` }}
      />

      {/* ── Glassmorphic card ── */}
      <div className="maint-card">
        {/* Badge */}
        <span className="maint-badge">Scheduled Maintenance</span>

        {/* Icon */}
        <MaintenanceIcon />

        {/* Title */}
        <h1 className="maint-title">
          We'll Be Right Back
        </h1>

        {/* Message */}
        <p className="maint-message">{message}</p>

        {/* Status steps */}
        <StatusSteps />

        {/* Countdown */}
        {showCountdown && <CountdownTimer estimatedMinutes={estimatedMinutes} />}

        {/* CTA */}
        <div className="maint-actions">
          <button className="maint-btn-primary" onClick={() => window.location.reload()}>
            <span className="maint-btn-shimmer" />
            <span className="maint-btn-text">Refresh Page</span>
          </button>
          <a
            href="https://status.scrudix.com"
            target="_blank"
            rel="noopener noreferrer"
            className="maint-btn-ghost"
          >
            Check Status Page →
          </a>
        </div>

        {/* Footer */}
        <p className="maint-footer-note">
          Questions? Reach us at{' '}
          <a href="mailto:support@scrudix.com" className="maint-mail-link">
            support@scrudix.com
          </a>
        </p>
      </div>

      {/* Subtle noise overlay */}
      <div className="maint-noise" aria-hidden="true" />
    </div>
  );
};

export default MaintenancePage;
